import { useRef, useState, useMemo, useEffect } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  flexRender,
} from "@tanstack/react-table";
import { Card, CardContent } from "@/components/ui/card";
import { PreviewImage } from "@/components/ui/image";
import { Typography, Link, Tooltip } from "@castlery/fortress";
import { externalFeatures } from "@/constant";
import { cn } from "@/lib/utils";
import { useChatStore } from "@/stores/chatStore";
import { formatCurrency } from "@/utils";
import { checkBeforeAddToCart } from "@/utils/cartHelper";
import { UtmUrlHelper } from "@/utils/utm";
import { useToast } from "@/hooks/useToast";
import { Conversation } from "@/types";
import { apiPost } from "@/utils/apiHelper";
import { CARD_CART_API } from "@/config/api";
import {
  BUTTON_TAG,
  CLICK_ACTION,
  TRACK_EVENT,
  LINK_TAG,
  trackEvent,
} from "@/utils/analytics";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorContent } from "./Message";
import { AddToCart } from "./AddToCart";
import { useMessageContext } from "./MessageContext";

type LabelType = "text" | "image" | "link";

type ComparisonData = {
  product_id: string;
  product_pic: string;
  product_name: string;
  product_original_price: string;
  product_price: string;
  product_link: string;
  other_display_fields: {
    label: string;
    value: string;
    type: LabelType;
  }[];
  product_sku_code: string;
  variant_id: string;
  sale_quantity_min: number;
};

type ComparisonRowData = {
  property: string;
  [key: string]:
    | string
    | { value: string; type: LabelType }
    | { [type: string]: { value: string; type: LabelType } };
};

type ComparisonTableProps = {
  conversation: Conversation;
  styles?: React.CSSProperties;
  data: ComparisonData[];
};

const formatLabel = (label = "") => {
  let normalized = label.trim().toLowerCase();

  // 特殊拼写修正
  normalized = normalized.replace(/waranty/g, "warranty");

  if (!normalized) return "";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const transformData = (data: ComparisonData[]): ComparisonRowData[] => {
  if (data.length === 0) return [];

  const rows: ComparisonRowData[] = [];
  const labelToProductsMap = new Map<
    string,
    {
      // 按 label 汇总每个 sku 的字段
      perProductFields: Map<
        string, // variant_id
        Array<{ type: string; value: string; fieldType: LabelType }>
      >;
      // 统计该 label 下各 type 的覆盖次数（有多少个 sku 提供了这个 type）
      typeCount: Map<string, number>;
    }
  >();

  const allSkuCount = data.length;

  // 1) 收集：按 label 聚合，每个 label 下收集各 sku 的字段，统计 type 覆盖次数
  data.forEach((product) => {
    const vid = product.variant_id;
    product.other_display_fields.forEach((field) => {
      const label = field.label;
      if (!labelToProductsMap.has(label)) {
        labelToProductsMap.set(label, {
          perProductFields: new Map(),
          typeCount: new Map(),
        });
      }
      const entry = labelToProductsMap.get(label)!;

      // 统计覆盖次数
      entry.typeCount.set(
        field.type,
        (entry.typeCount.get(field.type) || 0) + 1,
      );

      // 收集该 sku 在此 label 下的字段
      if (!entry.perProductFields.has(vid)) {
        entry.perProductFields.set(vid, []);
      }
      entry.perProductFields.get(vid)!.push({
        type: field.type,
        value: field.value,
        fieldType: field.type as LabelType,
      });
    });
  });

  // 2) 生成每个 label 的一行，行内每个 sku 的字段对象需按“公共type优先，差异type靠后”的顺序插入
  labelToProductsMap.forEach((entry, label) => {
    const row: ComparisonRowData = { property: formatLabel(label) };

    // 计算排序顺序：先公共（覆盖数===allSkuCount），后差异；同组内按 type 名称字典序
    const allTypes = Array.from(entry.typeCount.keys());
    const commonTypes = allTypes
      .filter((t) => entry.typeCount.get(t) === allSkuCount)
      .sort((a, b) => a.localeCompare(b));
    const diffTypes = allTypes
      .filter((t) => entry.typeCount.get(t)! < allSkuCount)
      .sort((a, b) => a.localeCompare(b));
    const orderedTypes = [...commonTypes, ...diffTypes];

    // 按照 orderedTypes 顺序，为每个 sku 构建字段对象（保持插入顺序）
    data.forEach((product) => {
      const vid = product.variant_id;
      const fields = entry.perProductFields.get(vid) || [];
      const typeToValue = new Map<string, { value: string; type: LabelType }>();
      fields.forEach((f) => {
        typeToValue.set(f.type, { value: f.value, type: f.fieldType });
      });

      const productFieldsOrdered: {
        [key: string]: { value: string; type: LabelType };
      } = {};

      orderedTypes.forEach((t) => {
        if (typeToValue.has(t)) {
          // 仅对该 sku 存在的类型插入，保持顺序
          productFieldsOrdered[formatLabel(t)] = typeToValue.get(t)!;
        }
      });

      row[vid] = productFieldsOrdered;
    });

    rows.push(row);
  });

  rows.sort((a, b) => {
    const isAOther = a.property?.toLowerCase() === "other" ? 1 : 0;
    const isBOther = b.property?.toLowerCase() === "other" ? 1 : 0;
    return isAOther - isBOther;
  });

  return rows;
};

const PropertyHeader: React.FC = () => {
  return (
    <div className="flex flex-col h-full items-center justify-center">
      <Typography level="caption1">Product name</Typography>
    </div>
  );
};

const ProductHeader: React.FC<{
  conversation: Conversation;
  product: ComparisonData;
}> = ({ conversation, product }) => {
  const { toast } = useToast();
  const { message_id, handleProductClick } = useMessageContext();
  const [waitingForAddToCart, setWaitingForAddToCart] = useState(false);

  const productObj = {
    id: product.product_id,
  };

  const variantObj = {
    id: product.variant_id,
    price: product.product_price,
    variant_option_values: [],
  };

  const utmLink = UtmUrlHelper.generateUtmUrl(
    product.product_link,
    product.product_name,
  );

  const handleAddToCart = async () => {
    trackEvent({
      event: TRACK_EVENT,
      category: "product_comparison",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "add_to_cart",
    });

    if (waitingForAddToCart) {
      return;
    }

    const quantity = 1;
    const canEnableAddToCart = checkBeforeAddToCart({
      product: productObj,
      variant: variantObj,
      quantity,
    });

    if (!canEnableAddToCart) {
      return;
    }

    setWaitingForAddToCart(true);
    try {
      const res = await externalFeatures.addToCart({
        product: productObj,
        variant: variantObj,
        quantity,
      });

      if (res) {
        toast({
          description: "Added to cart successfully",
        });
        apiPost(CARD_CART_API.addRecord(), {
          conversation_id: conversation.conversation_id,
          message_id,
          product_name: product.product_name,
          product_sku_code: product.product_sku_code,
          quantity,
        });
      }

      // throw new Error("Add to cart failed");
    } catch (error) {
      console.error(error);
      // NOTE: 不需要 toast 提示，外部第三方会提示用户
      // NOTE: 抛出错误，让 ErrorBoundary 捕获
      // throw error;
    } finally {
      setWaitingForAddToCart(false);
    }
  };

  return (
    <div
      className="flex flex-col h-full cursor-pointer"
      onClick={() => {
        trackEvent({
          event: TRACK_EVENT,
          category: "product_comparison",
          action: CLICK_ACTION,
          tag: LINK_TAG,
          tag_value: "product_link",
        });

        window.open(utmLink, "_blank");
        handleProductClick(product.product_sku_code);
      }}
    >
      <div className="flex-1">
        <div className="flex items-center justify-center">
          <div className="w-24 h-24">
            <PreviewImage
              src={product.product_pic}
              alt={product.product_name}
              allowZoom={false}
            />
          </div>
        </div>
        <div className="mb-3">
          <Tooltip
            title={product.product_name}
            arrow={true}
            placement="top"
            enterDelay={300}
            theme="dark"
          >
            <div
              className="line-clamp-2"
              onClick={() => {
                trackEvent({
                  event: TRACK_EVENT,
                  category: "product_comparison",
                  action: CLICK_ACTION,
                  tag: LINK_TAG,
                  tag_value: "product_link",
                });

                window.open(utmLink, "_blank");
              }}
            >
              <Typography level="caption1">{product.product_name}</Typography>
            </div>
          </Tooltip>
        </div>
      </div>
      <div className="flex items-center justify-between h-[40px]">
        <div className="flex flex-col">
          <Typography level="body2" className="space-x-1">
            {formatCurrency(Number(product.product_price), "SGD")}
            {product.sale_quantity_min > 1 && (
              <Typography level="caption1" className="text-[#9E9E9E]">
                (Set of {product.sale_quantity_min})
              </Typography>
            )}
          </Typography>
          {product.product_original_price !== product.product_price && (
            <Typography
              level="caption1"
              className="text-[#9E9E9E] line-through"
            >
              {formatCurrency(Number(product.product_original_price), "SGD")}
            </Typography>
          )}
        </div>
        <div>
          <AddToCart
            waitingForAddToCart={waitingForAddToCart}
            handleAddToCart={handleAddToCart}
          />
        </div>
      </div>
    </div>
  );
};

const generateColumns = (
  conversation: Conversation,
  data: ComparisonData[],
) => {
  const columnHelper = createColumnHelper<ComparisonRowData>();
  return [
    columnHelper.accessor("property", {
      id: "property",
      header: () => <PropertyHeader />,
      size: 90,
      cell: (info) => (
        <div className="p-2">
          <Typography level="caption1">{info.getValue()}</Typography>
        </div>
      ),
    }),
    ...data.map((product) =>
      columnHelper.accessor(product.variant_id, {
        header: () => (
          <ProductHeader conversation={conversation} product={product} />
        ),
        size: 160,
        cell: (info) => {
          const value = info.getValue();
          if (typeof value === "object" && value) {
            // 如果是聚合数据（包含多个 type）
            if (typeof value === "object" && !("value" in value)) {
              if (Object.keys(value).length === 0) {
                return (
                  <div className="p-2">
                    <Typography level="caption1">-</Typography>
                  </div>
                );
              }
              return (
                <div className="p-2 space-y-1">
                  {Object.entries(value).map(([type, fieldData]) => (
                    <div key={type} className="text-xs">
                      <span className="font-medium text-[#7C5F68]">
                        {type}:
                      </span>
                      <span className="ml-1 text-[#3C101E]">
                        {fieldData.value || "-"}
                      </span>
                    </div>
                  ))}
                </div>
              );
            }

            // 原有的单个字段处理逻辑
            if ("value" in value) {
              if (value.type === "image") {
                return (
                  <div className="p-2">
                    <div className="w-12 h-12">
                      <PreviewImage src={value.value as string} alt="" />
                    </div>
                  </div>
                );
              } else if (value.type === "link") {
                return (
                  <div className="p-2">
                    <a
                      href={value.value as string}
                      className="text-blue-500 underline text-[14px]"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Link
                    </a>
                  </div>
                );
              }
              return (
                <div className="p-2">
                  <Typography level="caption1">
                    {(value.value as string) || "-"}
                  </Typography>
                </div>
              );
            }
          }
          return (
            <div className="p-2">
              <Typography level="caption1">
                {(value as string) || "-"}
              </Typography>
            </div>
          );
        },
      }),
    ),
  ];
};

const ComparisonTableWithoutErrorBoundary: React.FC<ComparisonTableProps> = ({
  conversation,
  styles,
  data,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showHeaderShadow, setShowHeaderShadow] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const transformedData = useMemo(() => transformData(data), [data]);

  const columns = useMemo(() => {
    return generateColumns(conversation, data);
  }, [data, conversation]);
  const table = useReactTable({
    data: transformedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const showLeftShadow = container.scrollLeft > 0;
      const showHeaderShadow = container.scrollTop > 0;
      setShowLeftShadow(showLeftShadow);
      setShowHeaderShadow(showHeaderShadow);
    };

    // 初始检查
    handleScroll();

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, []);

  if (data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">No data to compare</div>
    );
  }

  const rows = table.getRowModel().rows;
  const shouldShowExpandButton = transformedData.length > 6;
  const visibleRows =
    shouldShowExpandButton && !isExpanded ? rows.slice(0, 6) : rows;

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const totalWidth = useMemo(() => {
    const firstColWidth = table.getAllColumns()[0]?.getSize() || 90;
    const otherColWidth = table.getAllColumns()[1]?.getSize() || 160;
    return firstColWidth + data.length * otherColWidth;
  }, [table, data.length]);

  return (
    <Card className="max-w-full border border-[#F6F3E7] bg-[#FBF9F4] text-[#3C101E] rounded-md rounded-tl-none">
      <CardContent className="p-[10px]">
        <div
          className="w-full rounded-lg border border-[#E0DDD2] overflow-hidden"
          style={styles}
        >
          <div ref={scrollContainerRef} className="relative overflow-x-auto">
            <div
              className="min-w-full"
              style={{
                width: `${totalWidth}px`,
              }}
            >
              {/* Header Row */}
              <div
                className={cn(
                  "grid sticky top-0 z-30",
                  showHeaderShadow && "header-shadow",
                )}
                style={{
                  gridTemplateColumns: `${
                    table.getAllColumns()[0]?.getSize() || 90
                  }px repeat(${data.length}, ${
                    table.getAllColumns()[1]?.getSize() || 160
                  }px)`,
                }}
              >
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header, index) => (
                    <div
                      key={header.id}
                      className={cn(
                        "p-2 border-b border-[#EDEDED] bg-[#FBF9F4]",
                        index === 0 && [
                          "sticky left-0 z-30 text-[#7C5F68]",
                          showLeftShadow && "left-sticky-shadow",
                        ],
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </div>
                  )),
                )}
              </div>

              {/* Body Rows */}
              <div className="relative">
                {visibleRows.map((row, rowIndex) => (
                  <div
                    key={row.id}
                    className="grid"
                    style={{
                      gridTemplateColumns: `${
                        table.getAllColumns()[0]?.getSize() || 90
                      }px repeat(${data.length}, ${
                        table.getAllColumns()[1]?.getSize() || 160
                      }px)`,
                    }}
                  >
                    {row.getVisibleCells().map((cell, cellIndex) => (
                      <div
                        key={cell.id}
                        className={cn({
                          "flex text-[#3C101E] transition-colors": true,
                          "items-center": cellIndex === 0,
                          "items-start": cellIndex !== 0,
                          "sticky left-0 z-10 text-[#7C5F68]": cellIndex === 0,
                          "left-sticky-shadow":
                            cellIndex === 0 && showLeftShadow,
                          "border-b border-[#EDEDED]":
                            visibleRows.length - 1 !== rowIndex,
                          "bg-[#FBF9F4]": rowIndex % 2 === 1,
                          "bg-[#FEFEFD]": rowIndex % 2 === 0,
                        })}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Expand Button */}
          {shouldShowExpandButton && (
            <div className="flex items-center justify-center py-3">
              <Link
                variant="secondary"
                size="xs"
                endDecorator={
                  isExpanded ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )
                }
                onClick={toggleExpanded}
              >
                {isExpanded ? "View less details" : "View more details"}
              </Link>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const ComparisonTable = ({
  conversation,
  styles,
  data,
}: ComparisonTableProps) => {
  return (
    <ErrorBoundary fallback={<ErrorContent position="left" />}>
      <ComparisonTableWithoutErrorBoundary
        conversation={conversation}
        styles={styles}
        data={data}
      />
    </ErrorBoundary>
  );
};
